import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ImagePlus } from "lucide-react";
import GenerateLoader from "@/components/ui/GenerateLoader";

export default function AIImageGen() {
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("image");
  const [size, setSize] = useState("1024x1024");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [library, setLibrary] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/ai/library"); setLibrary(data.items || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const gen = async (e, force = false) => {
    e?.preventDefault();
    if (!prompt) return toast.error("Enter a prompt");
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/image", { prompt, kind, size, force });
      setResult({ ...data.content, cached: data.cached });
      toast.success(data.cached ? "Reused a cached asset — 0 credits" : "Image ready");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const imgSrc = (item) => item?.url || (item?.data ? `data:image/png;base64,${item.data}` : "");

  return (
    <div className="space-y-6" data-testid="image-gen-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ ai image / infographic gen</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">Generate teaching visuals</h1>
        <p className="text-sm text-neutral-600 mt-1">10 credits per image. Created with OpenAI GPT Image 1.</p>
      </div>

      <form onSubmit={gen} className="bg-white border border-neutral-200 p-6 space-y-3">
        <textarea data-testid="image-prompt" rows={3} className={inp} placeholder="Describe the image, infographic or diagram you want..." value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        <div className="grid sm:grid-cols-3 gap-3">
          <select className={inp} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="image-kind">
            <option value="image">Image</option>
            <option value="infographic">Infographic</option>
            <option value="diagram">Diagram</option>
            <option value="presentation">Presentation slide</option>
          </select>
          <select className={inp} value={size} onChange={(e) => setSize(e.target.value)}>
            <option value="1024x1024">Square 1024×1024</option>
            <option value="1024x1536">Portrait 1024×1536</option>
            <option value="1536x1024">Landscape 1536×1024</option>
          </select>
          <button data-testid="image-generate-btn" type="submit" disabled={loading} className="bg-[#0A0C10] text-white font-bold py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Sparkles size={16} /> {loading ? <GenerateLoader label="Generating image" phrases={["Painting pixels…", "Adding detail…", "Adjusting light…"]} /> : "Generate"}
          </button>
        </div>
      </form>

      {result && (
        <div className="bg-white border border-neutral-200 p-6">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">/ result
            {result.cached && (
              <>
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full normal-case tracking-normal font-semibold" data-testid="image-cached-badge">Cached · 0 credits</span>
                <button type="button" onClick={(e) => gen(e, true)} className="text-[10px] underline text-neutral-500 hover:text-neutral-800 normal-case tracking-normal" data-testid="image-force-regen">Generate fresh (10 cr)</button>
              </>
            )}
          </div>
          <img src={imgSrc(result)} alt={result.prompt} className="max-w-full border border-neutral-200" />
        </div>
      )}

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">/ your library</div>
        {library.length === 0 ? (
          <div className="bg-white border border-neutral-200 p-6 text-center text-sm text-neutral-500">Nothing yet. Generate your first visual.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {library.map((item) => (
              <div key={item.id} className="bg-white p-3">
                {imgSrc(item) && <img src={imgSrc(item)} alt={item.title} className="w-full aspect-square object-cover" />}
                <div className="text-xs text-neutral-700 mt-2 font-medium truncate">{item.title}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">{item.kind}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
